import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';

import { type TabsArray } from '@vestigas/ui/molecules';

export type BaseTabViewProps<TView extends string> = { readonly view: TView };

/**
 * Custom hook for managing tab navigation with URL parameters
 * @template TTabProps - Props passed to the tab content component
 * @template TParams - Parameters for the route
 * @param routePath - The path of the route
 * @param defaultTab - The default tab to display
 * @param params - Parameters for the route
 * @param tabs - Array of tab definitions
 * @param options.isTabListSettled - Whether the tab list is final (default true).
 *   Pass false while an async gate (e.g. a query deciding a tab's visibility) is
 *   still pending: the invalid-tab redirect is deferred so a deep link to a
 *   not-yet-visible tab isn't redirected away before the list is known.
 * @returns An object containing the current tab, edit state, and navigation functions
 */
export function useUrlTabNavigation<
  TTabProps,
  TParams extends Record<string, string>,
>(
  routePath: string,
  defaultTab: string,
  params: TParams,
  tabs: TabsArray<TTabProps>,
  options?: { readonly isTabListSettled?: boolean },
) {
  const isTabListSettled = options?.isTabListSettled ?? true;
  const navigate = useNavigate();

  const { tab = defaultTab, view } = useSearch({ strict: false });

  const handleChangeTab = useCallback(
    (_: React.SyntheticEvent, tab: string) => {
      navigate({
        to: routePath,
        search: {
          tab,
          view: undefined,
        },
        params,
      });
    },
    [navigate, params, routePath],
  );

  const handleChangeView = useCallback(
    (view: string) => {
      navigate({
        to: routePath,
        search(previous) {
          return {
            ...previous,
            view,
          };
        },
        params,
      });
    },
    [navigate, params, routePath],
  );

  useEffect(() => {
    if (isTabListSettled && !tabs.some(({ value }) => value === tab)) {
      navigate({
        to: routePath,
        search: {
          tab: defaultTab,
          view: undefined,
        },
        params,
      });
    }
    // `tabs` must be a dependency so late filtering (e.g. a tab hidden once data
    // resolves) still redirects an invalid tab away.
  }, [defaultTab, isTabListSettled, navigate, params, routePath, tab, tabs]);

  const currentTab = tabs.find(({ value }) => value === tab);

  return {
    currentTab,
    handleChangeTab,
    handleChangeView,
    tab: tab as string | undefined,
    view: view as string | undefined,
  } as const;
}
